import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiCalendar, FiUser, FiTag, FiHeart, FiShare2, FiEye } from 'react-icons/fi';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SocialShareButtons from '../components/SocialShareButtons';
import { renderBlogContent } from '../utils/blogContentRenderer';
import { apiUrls, getHttpHeaders } from '../config/api';

// CSS Variables to match the site theme
const CSS_VARS = {
  primaryColor: '#253C8C',
  secondaryColor: '#F9D51D',
  textPrimary: '#1f2937',
  textSecondary: '#6b7280',
  textLight: '#9ca3af',
  bgLight: '#f8fafc',
  borderLight: '#e5e7eb',
  borderRadius: '8px',
  borderRadiusLarge: '12px',
  shadowLight: '0 1px 3px rgba(0, 0, 0, 0.1)',
  shadowMedium: '0 4px 6px rgba(0, 0, 0, 0.1)',
  gradientPrimary: 'linear-gradient(135deg, #253C8C 0%, #1e40af 100%)'
};

// Styled components for the blog detail page
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  padding: 20px 0;
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 2px solid #e5e7eb;
  color: #374151;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #253C8C;
    color: #253C8C;
    background-color: #f8fafc;
  }
`;

const BlogDetailContainer = styled.article`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const BlogHeader = styled.div`
  margin-bottom: 30px;
  text-align: center;
  border-bottom: 2px solid #f1f5f9;
  padding-bottom: 30px;
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #253C8C;
  margin-bottom: 20px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BlogMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  color: #6b7280;
  font-size: 14px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BlogImage = styled.div`
  width: 100%;
  height: 300px;
  margin-bottom: 30px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BlogContent = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #374151;
  
  h1, h2, h3, h4, h5, h6 {
    color: #253C8C;
    margin: 30px 0 15px 0;
    font-weight: 600;
  }
  
  h1 { font-size: 2rem; }
  h2 { font-size: 1.5rem; }
  h3 { font-size: 1.25rem; }
  
  p {
    margin-bottom: 20px;
  }
  
  ul, ol {
    margin-left: 20px;
    margin-bottom: 20px;
  }
  
  blockquote {
    border-left: 4px solid #253C8C;
    padding-left: 20px;
    margin: 20px 0;
    font-style: italic;
    color: #6b7280;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
  }
`;

const BlogActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
  padding-top: 30px;
  border-top: 2px solid #f1f5f9;
`;

const TagsSection = styled.div`
  margin: 30px 0;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
`;

const TagsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }
`;

const ShareSection = styled.div`
  margin: 30px 0;
  padding: 24px;
  background: white;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 2px solid #e5e7eb;
  color: #6b7280;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    border-color: #253C8C;
    color: #253C8C;
    background-color: #f8fafc;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
`;

interface BlogImage {
  id: number;
  image_filename: string;
  image_alt_text?: string;
  sort_order: number;
}

interface Blog {
  id: number;
  title: string;
  description: string;
  seo?: string;
  image: string;
  author?: string;
  category?: string;
  tags?: Array<{id?: number; name: string; slug?: string}> | string[];
  likes?: number;
  views?: number;
  created_at: string;
  updated_at: string;
  images?: BlogImage[];
  content_images?: BlogImage[]; // Backward compatibility
}

interface BlogDetailPageProps {
  blogId: string | null;
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick: () => void;
  onMkAcademyInPersonClick: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  onLogout: () => void;
}

const BlogDetailPage: React.FC<BlogDetailPageProps> = ({
  blogId,
  onHomeClick,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onLoginClick,
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [blogTags, setBlogTags] = useState<Array<{id: number; name: string; slug: string}>>([]);

  // Get API configuration (memoized to prevent re-creation)
  const blogServiceUrl = React.useMemo(
    () => process.env.REACT_APP_BLOG_SERVICE_URL || 'http://localhost:3011',
    []
  );

  const fetchBlogDetail = React.useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Fetching blog detail for ID:', id);
      console.log('🌐 API URL:', `${blogServiceUrl}/api/blogs/${id}`);
      
      const response = await fetch(`${blogServiceUrl}/api/blogs/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.blog) {
        console.log('📰 Blog detail loaded:', data.blog);
        console.log('📝 Blog description content:', data.blog.description);
        console.log('📝 Blog description length:', data.blog.description?.length);
        setBlog(data.blog);
        
        // Fetch tags for this blog
        try {
          const tagsResponse = await fetch(apiUrls.hashtags.blogTags(data.blog.id), {
            headers: getHttpHeaders()
          });
          if (tagsResponse.ok) {
            const tagsData = await tagsResponse.json();
            console.log('🏷️ Blog tags loaded:', tagsData.tags);
            setBlogTags(tagsData.tags || []);
          }
        } catch (tagError) {
          console.error('Error fetching blog tags:', tagError);
          setBlogTags([]);
        }
      } else if (data.blog) {
        // Handle case where API doesn't return success flag
        console.log('📰 Blog detail loaded (no success flag):', data.blog);
        console.log('📝 Blog description content:', data.blog.description);
        setBlog(data.blog);
        
        // Fetch tags for this blog
        try {
          const tagsResponse = await fetch(apiUrls.hashtags.blogTags(data.blog.id), {
            headers: getHttpHeaders()
          });
          if (tagsResponse.ok) {
            const tagsData = await tagsResponse.json();
            setBlogTags(tagsData.tags || []);
          }
        } catch (tagError) {
          console.error('Error fetching blog tags:', tagError);
          setBlogTags([]);
        }
      } else {
        console.log('❌ Blog API response:', data);
        throw new Error('Blog not found');
      }
    } catch (err) {
      console.error('Error fetching blog detail:', err);
      setError(err instanceof Error ? err.message : 'Failed to load blog details');
    } finally {
      setLoading(false);
    }
  }, [blogServiceUrl]);

  useEffect(() => {
    if (blogId) {
      fetchBlogDetail(blogId);
    } else {
      setError('No blog ID provided');
      setLoading(false);
    }
  }, [blogId, fetchBlogDetail]);

  const handleLikeClick = () => {
    console.log('👍 Like clicked for blog:', blog?.title);
    // TODO: Implement like functionality
  };

  const handleShareClick = () => {
    console.log('🔗 Share clicked for blog:', blog?.title);
    if (navigator.share && blog) {
      navigator.share({
        title: blog.title,
        text: blog.description ? blog.description.substring(0, 100) + '...' : 'Check out this blog post',
        url: window.location.href
      }).catch(console.error);
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        console.log('📋 URL copied to clipboard');
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <Header 
          onHomeClick={onHomeClick}
          onBooksClick={onBooksClick}
          onVideoLearningClick={onVideoLearningClick}
          onBlogsClick={onBlogsClick}
          onLoginClick={onLoginClick}
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <ContentContainer>
          <LoadingContainer>Loading blog details...</LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (error || !blog) {
    return (
      <PageContainer>
        <Header 
          onHomeClick={onHomeClick}
          onBooksClick={onBooksClick}
          onVideoLearningClick={onVideoLearningClick}
          onBlogsClick={onBlogsClick}
          onLoginClick={onLoginClick}
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
        />
        <ContentContainer>
          <BackButton onClick={onBlogsClick}>
            <FiArrowLeft size={16} />
            Back to Blogs
          </BackButton>
          <ErrorContainer>
            {error || 'Blog not found'}
          </ErrorContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <>
    <PageContainer>
      <Header 
        onHomeClick={onHomeClick}
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onLoginClick={onLoginClick}
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
      />
      <ContentContainer>
        <BackButton onClick={onBlogsClick}>
          <FiArrowLeft size={16} />
          Back to Blogs
        </BackButton>

        <BlogDetailContainer>
          {/* Blog Header */}
          <BlogHeader>
            <BlogTitle>{blog.title}</BlogTitle>
            
            <BlogMeta>
              <MetaItem>
                <FiCalendar size={16} />
                {formatDate(blog.created_at)}
              </MetaItem>
              
              {blog.author && (
                <MetaItem>
                  <FiUser size={16} />
                  {blog.author}
                </MetaItem>
              )}
              
              {blog.category && (
                <MetaItem>
                  <FiTag size={16} />
                  {blog.category}
                </MetaItem>
              )}
              
              {blog.views && (
                <MetaItem>
                  <FiEye size={16} />
                  {blog.views} views
                </MetaItem>
              )}
            </BlogMeta>
          </BlogHeader>

          {/* Blog Image */}
          {blog.image && (
            <BlogImage>
              <img 
                src={blog.image ? `/images/${blog.image}` : '/images/default-blog-thumbnail.jpg'}
                alt={blog.title}
                onError={(e) => {
                  const imgElement = e.target as HTMLImageElement;
                  // Prevent infinite loop - only set fallback once
                  if (!imgElement.dataset.errorHandled) {
                    console.warn('Failed to load blog image:', blog.image);
                    imgElement.dataset.errorHandled = 'true';
                    imgElement.src = '/images/default-blog-thumbnail.jpg';
                  }
                }}
              />
            </BlogImage>
          )}

          {/* Blog Content */}
          <BlogContent>
            {renderBlogContent({ 
              content: blog.description, 
              contentImages: blog.images || blog.content_images || [] 
            })}
          </BlogContent>

          {/* Tags Section */}
          {blogTags && blogTags.length > 0 && (
            <TagsSection>
              <TagsTitle>
                <FiTag /> Tags
              </TagsTitle>
              <TagsContainer>
                {blogTags.map((tag) => (
                  <TagBadge 
                    key={tag.id}
                    onClick={() => {
                      // Navigate to blogs filtered by this tag
                      console.log('Tag clicked:', tag.name);
                      // Future: implement tag filtering
                    }}
                    title={`View more blogs tagged with ${tag.name}`}
                  >
                    #{tag.name}
                  </TagBadge>
                ))}
              </TagsContainer>
            </TagsSection>
          )}

          {/* Social Share Section */}
          <ShareSection>
            <SocialShareButtons blog={{...blog, tags: blogTags}} showTitle={true} />
          </ShareSection>

          {/* Blog Actions */}
          <BlogActions>
            <ActionButton onClick={handleLikeClick}>
              <FiHeart />
              {blog.likes ? `${blog.likes} Likes` : 'Like'}
            </ActionButton>
            
            <ActionButton onClick={onBlogsClick}>
              <FiArrowLeft />
              Back to Blogs
            </ActionButton>
          </BlogActions>
        </BlogDetailContainer>
      </ContentContainer>
    </PageContainer>
    <Footer 
      onBooksClick={onBooksClick}
      onVideoLearningClick={onVideoLearningClick}
      onBlogsClick={onBlogsClick}
      onMkAcademyOnlineClick={onMkAcademyOnlineClick}
    />
    </>
  );
};

export default BlogDetailPage;
